import { useEffect, useMemo, useState } from "react";
import PageShell from "@/components/feature/PageShell";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import MovieCard from "@/components/feature/MovieCard";
import FilterPanel from "@/pages/category/components/FilterPanel";
import { movies } from "@/mocks/movies";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 12;

const SORTS = [
  { key: "year", label: "最新上映" },
  { key: "rating", label: "评分优先" },
  { key: "title", label: "标题排序" },
];

export default function Category() {
  usePageMeta({
    title: "色戒完整版无删减hd-影视分类",
    description:
      "色戒完整版无删减hd影视分类页，按内容类型、上映年代与题材标签筛选高清影片，查看封面、年份与剧情简介，快速找到想看的电影。",
    keywords: "影视分类,高清电影,电影筛选,影片列表,剧情片,纪录片,色戒完整版无删减hd",
  });

  const [type, setType] = useState("全部");
  const [decade, setDecade] = useState("全部");
  const [genre, setGenre] = useState("全部");
  const [sort, setSort] = useState("year");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [type, decade, genre, sort]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const filtered = useMemo(() => {
    const result = movies.filter((movie) => {
      const matchType = type === "全部" || movie.type === type;
      const matchDecade =
        decade === "全部" ||
        (decade === "2020年代" && movie.year >= 2020) ||
        (decade === "2010年代" && movie.year >= 2010 && movie.year < 2020) ||
        (decade === "2000年代" && movie.year >= 2000 && movie.year < 2010);
      const matchGenre = genre === "全部" || movie.genres.includes(genre);
      return matchType && matchDecade && matchGenre;
    });

    return [...result].sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "title") return a.title.localeCompare(b.title, "zh-Hans-CN");
      return b.year - a.year;
    });
  }, [type, decade, genre, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const resetAll = () => {
    setType("全部");
    setDecade("全部");
    setGenre("全部");
    setSort("year");
  };

  const filterProps = {
    type,
    decade,
    genre,
    onTypeChange: setType,
    onDecadeChange: setDecade,
    onGenreChange: setGenre,
    onReset: resetAll,
  };

  const activeCount =
    (type !== "全部" ? 1 : 0) + (decade !== "全部" ? 1 : 0) + (genre !== "全部" ? 1 : 0);

  return (
    <PageShell>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-shell px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视分类" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-[32px]">
            影视分类
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            按内容类型、上映年代与题材标签组合筛选，浏览影片封面、年份与简介摘要。每页展示 12 条，支持翻页浏览。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-shell px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
          <aside className="hidden lg:block">
            <div className="sticky top-[88px]">
              <FilterPanel {...filterProps} />
            </div>
          </aside>

          <div className="min-w-0">
            <div className="mb-5 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600 lg:hidden"
              >
                <i className="ri-filter-3-line" />
                筛选
                {activeCount > 0 && (
                  <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 font-label text-[10px] text-background-50">
                    {activeCount}
                  </span>
                )}
              </button>

              <p className="text-xs text-foreground-600">
                共找到 <span className="font-semibold text-foreground-950">{filtered.length}</span> 部影片
                {activeCount > 0 && <span className="ml-1 text-foreground-400">（已应用 {activeCount} 个筛选）</span>}
              </p>

              <div className="ml-auto flex items-center gap-2">
                <span className="hidden text-[11px] text-foreground-400 sm:inline">排序</span>
                <div className="flex rounded-md bg-background-100 p-1">
                  {SORTS.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setSort(item.key)}
                      className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1 text-[11px] transition-colors ${
                        sort === item.key
                          ? "bg-background-50 font-medium text-foreground-950"
                          : "text-foreground-500 hover:text-foreground-800"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {pageItems.length === 0 ? (
              <EmptyState
                icon="ri-film-line"
                title="没有符合当前筛选条件的影片"
                description="试着放宽上映年代或题材标签，或者直接清除所有筛选条件重新浏览。"
                actionLabel="清除筛选"
                onAction={resetAll}
              />
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 xl:grid-cols-4">
                {pageItems.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} showSummary />
                ))}
              </div>
            )}

            <Pagination page={current} totalPages={totalPages} onChange={setPage} />
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-primary-950/60 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="animate-fade-in absolute bottom-0 left-0 right-0 max-h-[86vh] overflow-y-auto rounded-t-xl bg-background-100 p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-heading text-sm font-semibold text-foreground-950">筛选影片</p>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭筛选"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <FilterPanel {...filterProps} />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-4 w-full cursor-pointer rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50"
            >
              查看 {filtered.length} 部结果
            </button>
          </div>
        </div>
      )}
    </PageShell>
  );
}